/**
 * Chain feedback (thumbs up/down) plus the structured negative-feedback
 * modal for ReasoningChat. `orgId`, `conversationId`, and `chains` are
 * accepted as parameters rather than closed over from component scope, so
 * the handlers never operate on a stale org/conversation/chain list.
 */

import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import type {
  FeedbackRating,
  StructuredFeedbackData,
} from '@/lib/reasoning/feedback';
import {
  removeChainFeedback,
  submitChainFeedback,
} from '@/lib/reasoning/feedback';

/** Minimal shape needed to seed the feedback modal's step list. */
export interface ChainFeedbackChain {
  id: string;
  steps?: Array<{ id: string; step_number: number }>;
}

export interface UseChainFeedbackModalParams {
  orgId: string | undefined;
  conversationId: string | undefined;
  chains: ChainFeedbackChain[];
}

export interface UseChainFeedbackModalResult {
  chainFeedback: Record<string, FeedbackRating | null>;
  feedbackModalOpen: boolean;
  feedbackModalChainId: string | null;
  feedbackModalSteps: Array<{ id: number; step_number: number }>;
  handleFeedback: (chainId: string, rating: FeedbackRating) => Promise<void>;
  handleStructuredFeedback: (data: StructuredFeedbackData) => Promise<void>;
  handleFeedbackModalClose: () => void;
}

export function useChainFeedbackModal({
  orgId,
  conversationId,
  chains,
}: UseChainFeedbackModalParams): UseChainFeedbackModalResult {
  const [chainFeedback, setChainFeedback] = useState<
    Record<string, FeedbackRating | null>
  >({});
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [feedbackModalChainId, setFeedbackModalChainId] = useState<
    string | null
  >(null);
  const [feedbackModalSteps, setFeedbackModalSteps] = useState<
    Array<{ id: number; step_number: number }>
  >([]);

  // Handle feedback submission (thumbs up/down)
  const handleFeedback = useCallback(
    async (chainId: string, rating: FeedbackRating) => {
      if (!orgId || !conversationId) {
        return;
      }

      const currentFeedback = chainFeedback[chainId];

      try {
        if (currentFeedback === rating) {
          // Toggle off - remove feedback
          await removeChainFeedback(
            parseInt(orgId),
            parseInt(conversationId),
            parseInt(chainId)
          );
          setChainFeedback(prev => ({ ...prev, [chainId]: null }));
        } else if (rating === 'negative') {
          // For negative feedback, open modal to collect details
          const chain = chains.find(c => String(c.id) === chainId);
          setFeedbackModalChainId(chainId);
          setFeedbackModalSteps(
            chain?.steps?.map(s => ({
              id: parseInt(s.id),
              step_number: s.step_number,
            })) || []
          );
          setFeedbackModalOpen(true);
        } else {
          // Positive feedback - submit directly
          await submitChainFeedback(
            parseInt(orgId),
            parseInt(conversationId),
            parseInt(chainId),
            rating
          );
          setChainFeedback(prev => ({ ...prev, [chainId]: rating }));
        }
      } catch (error) {
        console.error('Failed to submit feedback:', error);
      }
    },
    [orgId, conversationId, chainFeedback, chains]
  );

  // Handle structured feedback submission from modal
  const handleStructuredFeedback = useCallback(
    async (data: StructuredFeedbackData) => {
      const currentOrgId = orgId;
      const currentConversationId = conversationId;
      const currentFeedbackModalChainId = feedbackModalChainId;
      if (
        !currentOrgId ||
        !currentConversationId ||
        !currentFeedbackModalChainId
      ) {
        return;
      }

      try {
        await submitChainFeedback(
          parseInt(currentOrgId),
          parseInt(currentConversationId),
          parseInt(currentFeedbackModalChainId),
          data
        );
        setChainFeedback(prev => ({
          ...prev,
          [currentFeedbackModalChainId]: 'negative',
        }));
      } catch (error) {
        console.error('Failed to submit structured feedback:', error);
        toast.error("We couldn't submit that feedback. Try again.");
        throw error; // Re-throw to keep modal open on failure
      }
    },
    [orgId, conversationId, feedbackModalChainId]
  );

  // Handle feedback modal close
  const handleFeedbackModalClose = useCallback(() => {
    setFeedbackModalOpen(false);
    setFeedbackModalChainId(null);
  }, []);

  return useMemo(
    () => ({
      chainFeedback,
      feedbackModalOpen,
      feedbackModalChainId,
      feedbackModalSteps,
      handleFeedback,
      handleStructuredFeedback,
      handleFeedbackModalClose,
    }),
    [
      chainFeedback,
      feedbackModalOpen,
      feedbackModalChainId,
      feedbackModalSteps,
      handleFeedback,
      handleStructuredFeedback,
      handleFeedbackModalClose,
    ]
  );
}
